import React, { useEffect, useState } from "react"
import firebase from "firebase"
import moment from "moment"
import { Link } from "react-router-dom"
import { db, auth } from "../../../../utils/firebase"

import Composer from "./composer"
import { ACTIVITY_TYPES } from "./types"

/**
 * Resolve a uid → display name. Cached per uid for the session so the feed
 * doesn't pound Firestore when many entries share the same author.
 */
const userNameCache = {}
const resolveUserName = async (uid) => {
	if (!uid) return null
	if (userNameCache[uid]) return userNameCache[uid]
	const doc = await db.doc(`users/${uid}`).get()
	if (doc.exists) {
		const { first_name, last_name } = doc.data()
		const name = `${first_name || ""} ${last_name || ""}`.trim() || null
		userNameCache[uid] = name
		return name
	}
	return null
}

/**
 * Single entry in the activity feed. Owns its own reply expand/compose state.
 * `basePath` is the parent document path (e.g. `enquiries/{id}`).
 */
export default function ActivityEntry({ basePath, entry, readOnly = false, collectionName = "activity" }) {
	const [authorName, setAuthorName] = useState(null)
	const [replies, setReplies] = useState([])
	const [showReplies, setShowReplies] = useState(false)
	const [replying, setReplying] = useState(false)

	const meta = ACTIVITY_TYPES[entry.type] || { label: entry.type, badge: "" }

	useEffect(() => {
		let cancelled = false
		resolveUserName(entry.user).then((name) => {
			if (!cancelled) setAuthorName(name)
		})
		return () => {
			cancelled = true
		}
	}, [entry.user])

	useEffect(() => {
		if (!basePath || !entry.id) return
		const unsub = db
			.collection(`${basePath}/${collectionName}/${entry.id}/replies`)
			.orderBy("created", "asc")
			.onSnapshot((snap) => {
				setReplies(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
			})
		return () => unsub()
	}, [basePath, entry.id, collectionName])

	const saveReply = async ({ body }) => {
		await db.collection(`${basePath}/${collectionName}/${entry.id}/replies`).add({
			body,
			user: auth.currentUser?.uid || null,
			created: firebase.firestore.FieldValue.serverTimestamp(),
		})
		setReplying(false)
		setShowReplies(true)
	}

	const created = entry.created?.seconds ? moment(entry.created.seconds, "X") : null
	const relative = created ? created.fromNow() : ""
	const timestamp = created ? created.format("DD/MM/YYYY HH:mm") : "—"

	const Icon = meta.icon

	return (
		<li className="enquiry-feed-row">
			<div className="enquiry-feed-marker">
				{Icon ? (
					<span className={`enquiry-feed-icon-wrap is-type-${entry.type?.toLowerCase().replace(/_/g, "-") || ""}`} aria-hidden="true">
						<Icon size={14} strokeWidth={1.75} />
					</span>
				) : (
					<span className="enquiry-feed-note-dot" aria-hidden="true" />
				)}
			</div>

			<div className="enquiry-entry">
				<h4 className="enquiry-entry-type">{meta.label}</h4>

				{entry.body && <p className="enquiry-entry-body">{entry.body}</p>}

				{entry.link_to && entry.link_label && (
					<p className="enquiry-entry-body">
						<Link to={entry.link_to}>{entry.link_label}</Link>
					</p>
				)}

				<div className="enquiry-entry-footer">
					<span className="enquiry-entry-meta" title={relative}>
						<span className="enquiry-entry-author">
							{authorName || <small className="no-value">Unknown</small>}
						</span>
						<span className="enquiry-entry-meta-sep" aria-hidden="true">·</span>
						<span className="enquiry-entry-time">{timestamp}</span>
					</span>

					<div className="enquiry-entry-actions">
						{replies.length > 0 && (
							<button
								type="button"
								className="enquiry-entry-link"
								onClick={() => setShowReplies(!showReplies)}>
								{showReplies ? "Hide" : "Show"} {replies.length} {replies.length === 1 ? "note" : "notes"}
							</button>
						)}
						{!replying && !readOnly && (
							<button
								type="button"
								className="enquiry-entry-link"
								onClick={() => setReplying(true)}>
								+ add note
							</button>
						)}
					</div>
				</div>

				{showReplies && replies.length > 0 && (
					<div className="enquiry-entry-replies">
						{replies.map((reply) => (
							<Reply key={reply.id} reply={reply} />
						))}
					</div>
				)}

				{replying && !readOnly && (
					<div className="enquiry-entry-replies">
						<Composer
							type="NOTE"
							onSave={saveReply}
							onCancel={() => setReplying(false)}
							submitLabel="Add note"
						/>
					</div>
				)}
			</div>
		</li>
	)
}

function Reply({ reply }) {
	const [authorName, setAuthorName] = useState(null)

	useEffect(() => {
		let cancelled = false
		resolveUserName(reply.user).then((name) => {
			if (!cancelled) setAuthorName(name)
		})
		return () => {
			cancelled = true
		}
	}, [reply.user])

	const created = reply.created?.seconds ? moment(reply.created.seconds, "X") : null
	const relative = created ? created.fromNow() : ""
	const timestamp = created ? created.format("DD/MM/YYYY HH:mm") : "—"

	return (
		<div className="enquiry-reply">
			<p className="enquiry-reply-body">{reply.body}</p>
			<span className="enquiry-reply-meta" title={relative}>
				<span className="enquiry-reply-author">
					{authorName || <small className="no-value">Unknown</small>}
				</span>
				<span className="enquiry-reply-meta-sep" aria-hidden="true">·</span>
				<span className="enquiry-reply-time">{timestamp}</span>
			</span>
		</div>
	)
}
